import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Clock, X, Loader2, Car } from 'lucide-react';
import { API } from '../App';
import AppTopHeader from '../components/AppTopHeader';

const ChatPage = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const ownerToken = searchParams.get('token'); // Token from SMS link
  const qrCode = searchParams.get('qr'); // QR code for new chat
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [userRole, setUserRole] = useState('scanner'); // scanner or owner
  const [currentSessionId, setCurrentSessionId] = useState(sessionId);
  const chatLimit = session?.chat_message_limit || 5;
  const messagesEndRef = useRef(null);
  const pollInterval = useRef(null);

  const createNewChatSession = async () => {
    try {
      const response = await axios.post(`${API}/chat`, {
        qr_code: qrCode,
        initial_message: ''
      });

      if (response.data.success) {
        setCurrentSessionId(response.data.session_id);
        // Now load the chat data for the new session
        await loadChatData(false, response.data.session_id);
      } else {
        toast.error('Failed to create chat session');
        setTimeout(() => navigate('/'), 2000);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to create chat session';
      toast.error(errorMsg);
      setTimeout(() => navigate('/'), 2000);
    }
  };

  const loadChatData = async (silent = false, loadSessionId = null) => {
    const idToUse = loadSessionId || currentSessionId;
    if (!idToUse) return;

    try {
      // Include token in request if available
      const url = ownerToken 
        ? `${API}/chat/${idToUse}?token=${ownerToken}`
        : `${API}/chat/${idToUse}`;
      
      const response = await axios.get(url);
      setSession(response.data.session);
      setMessages(response.data.messages);
      
      // Set user role from server response
      if (response.data.user_role) {
        setUserRole(response.data.user_role);
      }
      
      // Check if session auto-closed due to inactivity
      if (response.data.session.auto_closed && !silent) {
        const otherParty = userRole === 'owner' ? 'Scanner' : 'Car owner';
        toast.error(`${otherParty} closed their browser. Chat session ended.`);
      }
      
      // Check if session is expired or closed
      if (!response.data.session.is_active) {
        if (!silent) {
          toast.error('Chat session has ended');
        }
        if (pollInterval.current) {
          clearInterval(pollInterval.current);
        }
      }
    } catch (error) {
      if (!silent) {
        toast.error('Failed to load chat');
        setTimeout(() => navigate('/'), 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // If accessing with QR code instead of session ID, create new session
    if (qrCode && !currentSessionId) {
      createNewChatSession();
    } else {
      loadChatData();

      // Poll for new messages every 3 seconds
      pollInterval.current = setInterval(() => {
        loadChatData(true);
      }, 3000);
    }

    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrCode]);

  // Handle browser close/navigation away - notify backend to close session
  useEffect(() => {
    if (!currentSessionId) return;

    const handleBeforeUnload = async (e) => {
      // Best effort to notify backend that user is leaving
      if (session?.is_active) {
        const url = ownerToken 
          ? `${API}/chat/${currentSessionId}/disconnect?token=${ownerToken}`
          : `${API}/chat/${currentSessionId}/disconnect`;
        
        // Use sendBeacon for reliability during page unload
        const blob = new Blob([JSON.stringify({})], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentSessionId, session, ownerToken]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) return;
    if (!session?.is_active) {
      toast.error('Chat session has ended');
      return;
    }

    setSending(true);
    try {
      // Use different endpoint based on user role
      if (userRole === 'owner' && ownerToken) {
        // Owner replying via token link
        await axios.post(`${API}/chat/${currentSessionId}/reply?token=${ownerToken}&content=${encodeURIComponent(newMessage)}`);
      } else {
        // Scanner or logged-in owner
        await axios.post(`${API}/chat/message`, {
          session_id: currentSessionId,
          sender: userRole,
          content: newMessage
        });
      }
      setNewMessage('');
      await loadChatData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
      if (error.response?.data?.detail?.includes('limit reached')) {
        setTimeout(() => loadChatData(true), 1000);
      }
    } finally {
      setSending(false);
    }
  };

  const endChat = async () => {
    if (!window.confirm('End this chat session?')) return;
    
    try {
      // Use token parameter if accessing via SMS link
      const url = ownerToken 
        ? `${API}/chat/${currentSessionId}/end?token=${ownerToken}`
        : `${API}/chat/${currentSessionId}/end`;
      
      await axios.post(url);
      toast.success('Chat ended');
      
      // Navigate based on how user accessed chat
      if (ownerToken) {
        // Token users came from SMS, show simple message
        navigate('/');
      } else {
        // Logged-in users go to dashboard
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Failed to end chat');
    }
  };

  const getTimeRemaining = () => {
    if (!session?.expires_at) return '';
    const now = new Date();
    const expires = new Date(session.expires_at);
    const diff = expires - now;
    
    if (diff <= 0) return 'Expired';
    
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <AppTopHeader
        backLabel="Back"
        rightSlot={userRole === 'owner' && session?.is_active ? (
          <button
            onClick={endChat}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 border border-red-100"
            data-testid="end-chat-btn"
          >
            <X className="w-4 h-4" />
            <span className="text-sm font-medium">End Chat</span>
          </button>
        ) : null}
      />

      <div className="bg-white border-b border-slate-200 shadow-sm flex-shrink-0">
        <div className="max-w-4xl mx-auto px-4 py-2">
          <div className="flex items-center space-x-2 text-sm">
            <div className={`w-2 h-2 rounded-full ${
              session?.is_active ? 'bg-emerald-500' : 'bg-red-500 animate-pulse'
            }`}></div>
            <span className={`font-medium ${
              session?.is_active ? 'text-slate-600' : 'text-red-600'
            }`}>
              {session?.is_active ? 'Active' : 'Ended'}
            </span>
            {session?.is_active && (
              <>
                <span className="text-slate-400">•</span>
                <Clock className="w-4 h-4 text-slate-400" />
                <span className="text-slate-600">{getTimeRemaining()}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Owner Welcome Banner */}
      {userRole === 'owner' && ownerToken && messages.length <= 1 && (
        <div className="bg-blue-50 border-b border-blue-100">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <div className="flex items-center space-x-3">
              <Car className="w-5 h-5 text-blue-600" />
              <p className="text-sm text-blue-800">
                Someone scanned your car&apos;s QR code and needs your help. Reply below.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Inactivity Warning */}
      {session?.other_party_inactive && session?.is_active && (
        <div className="bg-gradient-to-r from-orange-100 to-orange-50 border-b-2 border-orange-400">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 rounded-full bg-orange-500 animate-pulse"></div>
              <p className="text-sm font-medium text-orange-900">
                ⚠️ {userRole === 'owner' ? 'Scanner' : 'Car owner'} may have closed their browser - chat will end in a few seconds
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6">
          
          {/* Session Ended Banner - Prominent */}
          {!session?.is_active && (
            <div className="mb-6 bg-gradient-to-r from-red-500 to-red-600 rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-5 flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                    <X className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-1">
                    Chat Session Ended
                  </h3>
                  <p className="text-sm text-red-50">
                    {session?.auto_closed 
                      ? `${userRole === 'owner' ? 'Scanner' : 'Car owner'} closed their browser or left the chat`
                      : 'This conversation has been closed by the owner'}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${
                  msg.sender === userRole ? 'justify-end' : 'justify-start'
                }`}
                data-testid={`chat-message-${msg.id}`}
              >
                <div
                  className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                    msg.sender === userRole
                      ? 'bg-[#0DB504] text-white'
                      : 'bg-white border border-slate-200 text-slate-900'
                  }`}
                >
                  <p className="text-sm mb-1">{msg.content}</p>
                  <span className={`text-xs ${
                    msg.sender === userRole ? 'text-white/70' : 'text-slate-500'
                  }`}>
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Limit Warning */}
          {session && session.message_count >= chatLimit - 1 && session.is_active && (
            <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm text-orange-800 text-center">
                ⚠️ {chatLimit - session.message_count} message{chatLimit - session.message_count !== 1 ? 's' : ''} remaining
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Message Input */}
      {session?.is_active ? (
        <div className="bg-white border-t border-slate-200 flex-shrink-0">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <form onSubmit={sendMessage} className="flex space-x-3">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={userRole === 'owner' ? "Type your reply..." : "Type your message..."}
                className="flex-1 h-12 rounded-full border border-slate-200 px-6 text-base focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                data-testid="chat-input"
                disabled={!session.is_active || sending}
              />
              <button
                type="submit"
                disabled={!newMessage.trim() || sending || !session.is_active}
                className="h-12 w-12 rounded-full bg-[#0DB504] text-white hover:bg-[#0a9e03] transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="send-chat-btn"
              >
                {sending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </form>
          </div>
        </div>
      ) : (
        <div className="bg-red-50 border-t-4 border-red-500 flex-shrink-0">
          <div className="max-w-4xl mx-auto px-4 py-6">
            <div className="flex flex-col items-center justify-center space-y-3">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <X className="w-8 h-8 text-red-600" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-bold text-red-900 mb-1">Chat Session Ended</h3>
                <p className="text-sm text-red-700">
                  {session?.auto_closed 
                    ? `${userRole === 'owner' ? 'Scanner' : 'Car owner'} closed their browser or left the chat`
                    : 'This conversation has been closed'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
